import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms'
import { MatDialogRef } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatSnackBar } from '@angular/material/snack-bar'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { catchError, finalize, forkJoin, of, tap } from 'rxjs'

import { BiztelPhoneNumberTagMappingInput } from '../../../../../../_generated/graphql'
import { ButtonComponent } from '../../../../../components/button/button.component'
import { LoadingComponent } from '../../../../../components/loading/loading.component'
import {
  Item,
  MultipleSelectComponent,
} from '../../../../../components/multiple-select/multiple-select.component'
import { AssessmentService } from '../../../../../services/assessment/assesssment.service'
import { AssessmentTagMaster } from '../../../../../services/assessment/assessment.mapping'
import {
  BiztelPhoneNumberTagMappingService,
  BiztelPhoneNumberTagMappingView,
} from '../../../../../services/external-integration/biztel-integration/biztel-phone-number-tag-mapping.service'
import { captureException } from '../../../../../util/captureException'

type BiztelTagMappingRowGroup = FormGroup<{
  phoneNumber: FormControl<string>
  tagMasterIds: FormControl<string[]>
}>

const PHONE_NUMBER_MAX_LENGTH = 20

// サーバー側 (saveBiztelPhoneNumberTagMappings) と同一規則: trim 後に数字のみ・
// 最大 20 桁。先頭 0 は許容 (通知番号 050〜 等はゼロ始まり)。
const phoneNumberValidator = (
  control: AbstractControl<string>,
): ValidationErrors | null => {
  const value = control.value.trim()
  if (value === '') {
    return { required: true }
  }
  if (!/^[0-9]+$/.test(value)) {
    return { digitsOnly: true }
  }
  if (value.length > PHONE_NUMBER_MAX_LENGTH) {
    return { maxLength: true }
  }
  return null
}

// セクション内の番号重複はサーバーの (isOutbound, phoneNumber) 重複エラーと同一規則。
// 重複している番号の集合をエラー値に持たせ、どの行が重複かの表示
// (isDuplicatePhoneNumber) はこの集合の参照だけで済ませる (テンプレートから
// 毎 CD 呼ばれるため、行数 n に対して全行走査 O(n²) にしない)。
const duplicatePhoneNumbersValidator = (
  control: AbstractControl,
): ValidationErrors | null => {
  const seen = new Set<string>()
  const duplicates = new Set<string>()
  for (const row of (control as FormArray<BiztelTagMappingRowGroup>).controls) {
    const value = row.controls.phoneNumber.value.trim()
    if (value === '') {
      continue
    }
    if (seen.has(value)) {
      duplicates.add(value)
    }
    seen.add(value)
  }
  return duplicates.size > 0 ? { duplicatePhoneNumbers: { duplicates } } : null
}

// backend の BIZTEL_PHONE_NUMBER_TAG_MAX_PAIR_COUNT と同値。
const TOTAL_PAIR_MAX_COUNT = 1000

// サーバーの総ペア数上限 (方向×番号×タグの行数 ≤ 1000) のミラー。超過は再試行で
// 直らない恒久的エラーのため、保存後の汎用失敗 snackbar ではなく事前にフォーム
// エラーとして示す。
const totalPairCountValidator = (
  control: AbstractControl,
): ValidationErrors | null => {
  const group = control as FormGroup<{
    inbound: FormArray<BiztelTagMappingRowGroup>
    outbound: FormArray<BiztelTagMappingRowGroup>
  }>
  const totalPairCount = [
    ...group.controls.inbound.controls,
    ...group.controls.outbound.controls,
  ].reduce((sum, row) => sum + row.controls.tagMasterIds.value.length, 0)
  return totalPairCount > TOTAL_PAIR_MAX_COUNT
    ? { totalPairCountExceeded: true }
    : null
}

/**
 * BIZTEL 自社側番号 → 商談タグ マッピングの設定ダイアログ。
 * 外部連携ページの「連携タグ設定」ボタンから開く (連携設定モーダルとは独立。
 * タグ保存に apiToken 再入力を要求しないための分離)。
 * 部品が自前で fetch するため MAT_DIALOG_DATA は受け取らない (account-mapping と同型)。
 */
@UntilDestroy()
@Component({
  selector: 'app-biztel-phone-number-tag-mapping-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatIcon,
    ButtonComponent,
    LoadingComponent,
    MultipleSelectComponent,
  ],
  templateUrl: './biztel-phone-number-tag-mapping-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './biztel-phone-number-tag-mapping-dialog.component.scss',
})
export class BiztelPhoneNumberTagMappingDialogComponent implements OnInit {
  assessmentTagMasters: AssessmentTagMaster[] = []
  isLoading = false
  isSaving = false
  loadError: string | null = null

  inboundMappings = new FormArray<BiztelTagMappingRowGroup>([], {
    validators: [duplicatePhoneNumbersValidator],
  })
  outboundMappings = new FormArray<BiztelTagMappingRowGroup>([], {
    validators: [duplicatePhoneNumbersValidator],
  })
  formGroup = new FormGroup(
    {
      inbound: this.inboundMappings,
      outbound: this.outboundMappings,
    },
    { validators: [totalPairCountValidator] },
  )

  // 着信 = 窓口 (queue) の内線番号 / 発信 = 相手に表示される通知番号 (ビジネス側の呼称)。
  readonly sections = [
    {
      title: '着信',
      numberLabel: '内線番号',
      description: '顧客がかけた先の窓口の番号です。',
      placeholder: '例: 7000',
      formArray: this.inboundMappings,
    },
    {
      title: '発信',
      numberLabel: '通知番号',
      description: '相手に表示される自社の番号です。',
      placeholder: '例: 05012345678',
      formArray: this.outboundMappings,
    },
  ] as const

  constructor(
    private dialogRef: MatDialogRef<BiztelPhoneNumberTagMappingDialogComponent>,
    private snackBar: MatSnackBar,
    private assessmentService: AssessmentService,
    private biztelPhoneNumberTagMappingService: BiztelPhoneNumberTagMappingService,
  ) {}

  get hasNoTagMasters(): boolean {
    return (
      !this.isLoading &&
      this.loadError == null &&
      this.assessmentTagMasters.length === 0
    )
  }

  ngOnInit(): void {
    this.isLoading = true
    forkJoin({
      tagMasters: this.assessmentService.getAssessmentTagMasters(),
      mappings:
        this.biztelPhoneNumberTagMappingService.getBiztelPhoneNumberTagMappings(),
    })
      .pipe(
        tap(({ tagMasters, mappings }) => {
          this.assessmentTagMasters = tagMasters
          this.setMappingRows(mappings)
        }),
        catchError((error: unknown) => {
          captureException(error)
          // 保存は全置換のため、既存マッピングを取得できないまま保存させると
          // 消すつもりのない行まで消しうる。取得失敗時はフォーム全体を
          // 出さず、持続表示で再読込を促す。
          this.loadError =
            'タグ設定の取得に失敗しました。ページを再読込してください。'
          return of(null)
        }),
        finalize(() => {
          this.isLoading = false
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  onSubmit(event: Event): void {
    event.preventDefault()
    // 二重送信ガード。保存ボタン disabled + loading overlay で実経路は無いが、
    // 送信経路 (Enter キー等) の変化に依存しない防御として置く。
    if (this.isSaving) {
      return
    }
    this.formGroup.markAllAsTouched()

    if (this.formGroup.invalid) {
      this.snackBar.open('入力内容を確認してください。', '閉じる', {
        duration: 4000,
      })
      return
    }

    // 保存は org 単位の全置換 (mappings: [] は全削除)。両セクションの全行に
    // isOutbound を焼き込み、着信・発信まとめて 1 回で送る。
    const mappings: BiztelPhoneNumberTagMappingInput[] = [
      ...this.inboundMappings.controls.map((row) =>
        this.toMappingInput(row, false),
      ),
      ...this.outboundMappings.controls.map((row) =>
        this.toMappingInput(row, true),
      ),
    ]

    this.isSaving = true
    this.biztelPhoneNumberTagMappingService
      .saveBiztelPhoneNumberTagMappings({ mappings })
      .pipe(
        tap(() => {
          this.snackBar.open('連携タグ設定を保存しました。', '閉じる', {
            duration: 4000,
          })
          this.dialogRef.close()
        }),
        catchError((error: unknown) => {
          captureException(error)
          this.snackBar.open(
            '連携タグ設定の保存に失敗しました。時間をおいて再度お試しください。',
            '閉じる',
            { duration: 6000 },
          )
          return of(null)
        }),
        finalize(() => {
          this.isSaving = false
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  addTagMappingRow(section: FormArray<BiztelTagMappingRowGroup>): void {
    section.push(this.createTagMappingRow('', []))
  }

  removeTagMappingRow(
    section: FormArray<BiztelTagMappingRowGroup>,
    index: number,
  ): void {
    section.removeAt(index)
  }

  // FormArray 側の duplicatePhoneNumbers エラー (重複番号の集合) を
  // 「どの行が重複か」に落とすヘルパー。走査は validator 側で済んでいる。
  isDuplicatePhoneNumber(
    section: FormArray<BiztelTagMappingRowGroup>,
    index: number,
  ): boolean {
    const error = section.errors?.['duplicatePhoneNumbers'] as
      | { duplicates: Set<string> }
      | undefined
    if (!error) {
      return false
    }
    const value = section.at(index).controls.phoneNumber.value.trim()
    return value !== '' && error.duplicates.has(value)
  }

  tagItemsForRow(row: BiztelTagMappingRowGroup): Item[] {
    const selectedIds = new Set(row.controls.tagMasterIds.value)
    return this.assessmentTagMasters.map((master) => ({
      id: master.id,
      label: master.name,
      checked: selectedIds.has(master.id),
    }))
  }

  onTagSelectionChange(row: BiztelTagMappingRowGroup, items: Item[]): void {
    const control = row.controls.tagMasterIds
    control.setValue(
      items
        .filter((item) => item.checked)
        .map((item) => item.id)
        .filter((id): id is string => id != null),
    )
    control.markAsTouched()
  }

  close(): void {
    this.dialogRef.close()
  }

  private setMappingRows(mappings: BiztelPhoneNumberTagMappingView[]): void {
    this.inboundMappings.clear()
    this.outboundMappings.clear()
    const knownTagMasterIds = new Set(
      this.assessmentTagMasters.map((master) => master.id),
    )
    for (const mapping of mappings) {
      // 選択肢 (assessmentTagMasters) に無い id は表示から落とす。2 query 間の
      // ごく短い race で直後に無効化されたマスターを指すケースで、保存時にも
      // サーバー validation で弾かれる値のための仕様上の除外 (握り潰しではない)。
      // 落とした行が次回保存の全置換で消えるのも設計どおり: get は無効マスターの
      // 行を返さず、死に行は保存で自然に落とす方針 (設計ノート §3.1)。
      const tagMasterIds = mapping.tagMasterIds.filter((id) =>
        knownTagMasterIds.has(id),
      )
      if (tagMasterIds.length === 0) {
        continue
      }
      const row = this.createTagMappingRow(mapping.phoneNumber, tagMasterIds)
      if (mapping.isOutbound) {
        this.outboundMappings.push(row)
      } else {
        this.inboundMappings.push(row)
      }
    }
  }

  private createTagMappingRow(
    phoneNumber: string,
    tagMasterIds: string[],
  ): BiztelTagMappingRowGroup {
    return new FormGroup({
      phoneNumber: new FormControl(phoneNumber, {
        nonNullable: true,
        validators: [phoneNumberValidator],
      }),
      // 空配列 (タグ未選択行) はサーバー側もエラーのため UI 側で送らせない。
      tagMasterIds: new FormControl(tagMasterIds, {
        nonNullable: true,
        validators: [Validators.required],
      }),
    })
  }

  private toMappingInput(
    row: BiztelTagMappingRowGroup,
    isOutbound: boolean,
  ): BiztelPhoneNumberTagMappingInput {
    return {
      isOutbound,
      phoneNumber: row.controls.phoneNumber.value.trim(),
      tagMasterIds: row.controls.tagMasterIds.value,
    }
  }
}
